import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FlatSiteTreeNode } from '../lib/siteTree';
import { siteOptionLabel } from '../lib/siteTree';
import { cn } from '../lib/utils';

interface SiteTreeSelectProps {
  rows: FlatSiteTreeNode[];
  value: number | null;
  onChange: (siteId: number) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
  maxVisibleRows?: number;
}

export const SiteTreeSelect: React.FC<SiteTreeSelectProps> = ({
  rows,
  value,
  onChange,
  placeholder = 'Select site...',
  emptyLabel = 'No sites available',
  disabled = false,
  className,
  maxVisibleRows = 10,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedRow = useMemo(
    () => rows.find((row) => row.node.id === value) || null,
    [rows, value]
  );
  const isDisabled = disabled || rows.length === 0;

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  useEffect(() => {
    if (isDisabled) {
      setOpen(false);
    }
  }, [isDisabled]);

  const handleSelect = (siteId: number) => {
    onChange(siteId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={cn('truncate', !selectedRow && 'text-gray-500')}>
          {selectedRow ? siteOptionLabel(selectedRow) : rows.length === 0 ? emptyLabel : placeholder}
        </span>
        <span className="shrink-0 text-xs text-gray-400">{open ? '^' : 'v'}</span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white py-1 shadow-lg"
          style={{ maxHeight: `${maxVisibleRows * 34}px` }}
        >
          {rows.map((row) => {
            const selected = row.node.id === value;
            return (
              <button
                key={row.node.id}
                type="button"
                onClick={() => handleSelect(row.node.id)}
                title={row.path.join(' / ')}
                className={cn(
                  'flex min-h-[34px] w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50',
                  selected && 'bg-blue-50 text-blue-700'
                )}
              >
                <span className="min-w-0 flex-1 truncate">{siteOptionLabel(row)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
